import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SURFACE_HOVER, SURFACE_TINT, YELLOW } from 'styles/tokens';
import type { ChatHistoryItem } from 'utils/chatStorage';

interface ChatHistorySidebarProps {
  companyIdentifier: string;
  currentPhoneIdentifier: string | null;
  chatHistory: ChatHistoryItem[];
  onSelectChat: (phoneIdentifier: string) => void;
  onDeleteChat: (phoneIdentifier: string) => void;
  disableNewChatButton?: boolean;
  onNewChat?: () => void;
}

/**
 * Calculate days ago from timestamp
 *
 * @param timestamp - The timestamp to calculate from
 * @returns Number of days ago
 */
const getDaysAgo = (timestamp: number): number => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Format days ago text
 *
 * @param daysAgo - Number of days ago
 * @returns Formatted text
 */
const formatDaysAgo = (daysAgo: number): string => {
  if (daysAgo === 0) return 'Hoy';
  if (daysAgo === 1) return 'Ayer';
  return `Hace ${daysAgo} días`;
};

const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  companyIdentifier,
  currentPhoneIdentifier,
  chatHistory,
  onSelectChat,
  onDeleteChat,
  disableNewChatButton = false,
  onNewChat,
}) => {
  const navigate = useNavigate();
  const [hoveredChatId, setHoveredChatId] = React.useState<string | null>(null);
  const [hoveredNewChat, setHoveredNewChat] = React.useState(false);

  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      navigate(`/demochat/${companyIdentifier}/newchat`);
    }
  };

  /**
   * Open chat in a new tab
   *
   * @param e - Mouse event
   * @param phoneIdentifier - The phone identifier to load (optional for new chat)
   */
  const handleOpenInNewTab = (e: React.MouseEvent, phoneIdentifier?: string) => {
    e.stopPropagation();

    const path = phoneIdentifier
      ? `/demochat/${companyIdentifier}?loadChat=${phoneIdentifier}`
      : `/demochat/${companyIdentifier}/newchat`;
    window.open(path, '_blank');
  };

  const handleDeleteClick = (e: React.MouseEvent, phoneIdentifier: string) => {
    e.stopPropagation();
    onDeleteChat(phoneIdentifier);
  };

  return (
    <Box
      sx={{
        width: '400px',
        height: '100vh',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* New Chat Button */}
      <Box sx={{ p: 2 }} onMouseEnter={() => setHoveredNewChat(true)} onMouseLeave={() => setHoveredNewChat(false)}>
        <Box sx={{ position: 'relative' }}>
          <Button
            onClick={handleNewChat}
            fullWidth
            variant="contained"
            disabled={disableNewChatButton}
            startIcon={<AddIcon />}
            sx={{
              justifyContent: 'center',
              px: 2,
              py: 1,
              borderRadius: RADIUS_COIN,
              bgcolor: NAVY,
              color: '#fff',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: NAVY, opacity: 0.9 },
              '&.Mui-disabled': { opacity: 0.5, bgcolor: NAVY, color: '#fff' },
            }}
          >
            NUEVO CHAT
          </Button>
          {hoveredNewChat && !disableNewChatButton && (
            <IconButton
              size="small"
              onClick={(e) => handleOpenInNewTab(e)}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: YELLOW },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      {/* Chat List */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, pb: 2 }}>
        {chatHistory.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: NAVY,
                opacity: 0.5,
                fontFamily: 'Jost, sans-serif',
              }}
            >
              No hay chats previos
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {chatHistory.map((chat) => {
              const isCurrentChat = chat.phoneIdentifier === currentPhoneIdentifier;
              const displayText = chat.description || (isCurrentChat ? 'Chat actual' : 'Chat sin título');
              const isHovered = hoveredChatId === chat.phoneIdentifier;
              const daysAgo = getDaysAgo(chat.timestamp);
              const daysAgoText = formatDaysAgo(daysAgo);

              return (
                <ListItem
                  key={chat.phoneIdentifier}
                  disablePadding
                  onMouseEnter={() => !isCurrentChat && setHoveredChatId(chat.phoneIdentifier)}
                  onMouseLeave={() => !isCurrentChat && setHoveredChatId(null)}
                  secondaryAction={
                    isHovered && !isCurrentChat ? (
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenInNewTab(e, chat.phoneIdentifier)}
                          sx={{ color: NAVY, opacity: 0.6, '&:hover': { color: NAVY, opacity: 1 } }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => handleDeleteClick(e, chat.phoneIdentifier)}
                          sx={{ color: NAVY, opacity: 0.6, '&:hover': { color: 'error.main', opacity: 1 } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null
                  }
                  sx={{
                    '& .MuiListItemSecondaryAction-root': {
                      right: 4,
                    },
                  }}
                >
                  <ListItemButton
                    selected={isCurrentChat}
                    disabled={isCurrentChat}
                    onClick={() => !isCurrentChat && onSelectChat(chat.phoneIdentifier)}
                    sx={{
                      borderRadius: RADIUS_ITEM,
                      py: 1.25,
                      px: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: SURFACE_HOVER,
                      },
                      '&:hover': {
                        backgroundColor: isCurrentChat ? SURFACE_HOVER : SURFACE_TINT,
                        cursor: isCurrentChat ? 'default' : 'pointer',
                      },
                      '&.Mui-disabled': {
                        opacity: 1,
                      },
                    }}
                  >
                    <ListItemText
                      primary={displayText}
                      secondary={daysAgoText}
                      slotProps={{
                        primary: {
                          noWrap: true,
                          sx: {
                            fontSize: '0.875rem',
                            fontWeight: isCurrentChat ? 600 : 500,
                            color: NAVY,
                            fontFamily: 'Jost, sans-serif',
                          },
                        },

                        secondary: {
                          sx: {
                            fontSize: '0.75rem',
                            color: NAVY,
                            opacity: 0.55,
                            fontFamily: 'Jost, sans-serif',
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ChatHistorySidebar;
